import React, { useState, useEffect } from 'react';
import { PlusCircle, Edit2, Trash2, Save, X, ChevronDown, ChevronUp, Shield, Clock, Award } from 'lucide-react';
import { API_URL } from '../services/api';

// Types
interface Certification {
  id?: number;
  name: string;
  code: string;
  tier_level: number;
  department: string | null;
  training_link: string;
  validity_period: number | null; // in months, null = lifetime
  upload_required: boolean;
  is_active: boolean;
}

// Removed TierRequirement interface - no longer needed
// All certifications are automatically tier requirements based on their tier_level

// Constants
const BRANCHES = ['Portland', 'Seattle', 'Anchorage', 'Tri-Cities'];
const DEPARTMENTS = ['Service', 'Testing', 'Construction', 'Electricians', 'All'];
const TIER_LEVELS = [0, 1, 2, 3];
const VALIDITY_OPTIONS = [
  { label: 'Lifetime', value: null },
  { label: '1 Year', value: 12 },
  { label: '2 Years', value: 24 },
  { label: '3 Years', value: 36 },
  { label: '5 Years', value: 60 }
];

export default function CertificationAdmin() {
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCert, setEditingCert] = useState<Certification | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState('All');
  const [expandedTier, setExpandedTier] = useState<number | null>(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // New certification form state
  const [newCert, setNewCert] = useState<Certification>({
    name: '',
    code: '',
    tier_level: 0,
    department: null,
    training_link: '',
    validity_period: null,
    upload_required: true,
    is_active: true
  });

  useEffect(() => {
    fetchCertifications();
  }, []);

  const fetchCertifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/certifications`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch certifications');
      }
      
      const data = await response.json();
      setCertifications(Array.isArray(data) ? data : []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching certifications:', err);
      setError('Failed to load certifications');
      setCertifications([]); // Ensure empty array on error
      setLoading(false);
    }
  };

  // Removed fetchTierRequirements - no longer needed
  // All certifications are automatically tier requirements

  const handleAddCertification = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/certifications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newCert)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create certification');
      }

      const data = await response.json();
      setCertifications([...certifications, data.certification]);
      
      setNewCert({
        name: '',
        code: '',
        tier_level: 0,
        department: null,
        training_link: '',
        validity_period: null,
        upload_required: true,
        is_active: true
      });
      setShowAddForm(false);
      setError('');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleUpdateCertification = async () => {
    if (!editingCert) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/certifications/${editingCert.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editingCert)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update certification');
      }

      const data = await response.json();
      setCertifications(certifications.map(cert => 
        cert.id === editingCert.id ? data.certification : cert
      ));
      
      
      setEditingCert(null);
      setError('');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteCertification = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this certification?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/certifications/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete certification');
      }

      setCertifications(certifications.filter(cert => cert.id !== id));
      
      
      setError('');
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Removed toggleTierRequirement and isRequired functions - no longer needed
  // All certifications are automatically tier requirements when created

  const getValidityLabel = (months: number | null): string => {
    if (months === null) return 'Lifetime';
    if (months === 12) return '1 Year';
    if (months === 24) return '2 Years';
    if (months === 36) return '3 Years';
    if (months === 60) return '5 Years';
    return `${months} months`;
  };

  const getTierColor = (tier: number): string => {
    switch(tier) {
      case 1: return 'bg-green-600';
      case 2: return 'bg-blue-600';
      case 3: return 'bg-purple-600';
      default: return 'bg-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="bg-gray-800 rounded-lg shadow-xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-white">Certification Management</h2>
              <p className="text-gray-400 mt-1">Define certifications and tier requirements</p>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <PlusCircle className="h-5 w-5" />
              Add Certification
            </button>
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-600 bg-opacity-20 border border-red-600 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {/* Add/Edit Form */}
        {(showAddForm || editingCert) && (
          <div className="p-6 bg-gray-900 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">
              {editingCert ? 'Edit Certification' : 'New Certification'}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                <input
                  type="text"
                  value={editingCert ? editingCert.name : newCert.name}
                  onChange={(e) => editingCert 
                    ? setEditingCert({...editingCert, name: e.target.value})
                    : setNewCert({...newCert, name: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500"
                  placeholder="e.g., OSHA 10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Code</label>
                <input
                  type="text"
                  value={editingCert ? editingCert.code : newCert.code}
                  onChange={(e) => editingCert
                    ? setEditingCert({...editingCert, code: e.target.value})
                    : setNewCert({...newCert, code: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500"
                  placeholder="e.g., OSHA10"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Tier Level</label>
                <select
                  value={editingCert ? editingCert.tier_level : newCert.tier_level}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    editingCert
                      ? setEditingCert({...editingCert, tier_level: value})
                      : setNewCert({...newCert, tier_level: value});
                  }}
                  className="w-full px-3 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500"
                >
                  {TIER_LEVELS.map(tier => (
                    <option key={tier} value={tier}>{tier === 0 ? 'New Hire (Tier 0)' : `Tier ${tier}`}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Department</label>
                <select
                  value={editingCert ? editingCert.department || '' : newCert.department || ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : e.target.value;
                    editingCert
                      ? setEditingCert({...editingCert, department: value})
                      : setNewCert({...newCert, department: value});
                  }}
                  className="w-full px-3 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500"
                >
                  <option value="">All Departments</option>
                  {DEPARTMENTS.filter(dept => dept !== 'All').map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Validity Period</label>
                <select
                  value={editingCert ? editingCert.validity_period || '' : newCert.validity_period || ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? null : parseInt(e.target.value);
                    editingCert
                      ? setEditingCert({...editingCert, validity_period: value})
                      : setNewCert({...newCert, validity_period: value});
                  }}
                  className="w-full px-3 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500"
                >
                  {VALIDITY_OPTIONS.map(option => (
                    <option key={option.label} value={option.value || ''}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Training Link</label>
                <input
                  type="url"
                  value={editingCert ? editingCert.training_link : newCert.training_link}
                  onChange={(e) => editingCert
                    ? setEditingCert({...editingCert, training_link: e.target.value})
                    : setNewCert({...newCert, training_link: e.target.value})}
                  className="w-full px-3 py-2 bg-gray-800 text-white rounded-lg border border-gray-700 focus:border-blue-500"
                  placeholder="https://..."
                />
              </div>
            </div>
            
            {/* Upload Required Checkbox */}
            <div className="mt-4">
              <label className="flex items-center space-x-2 text-gray-300">
                <input
                  type="checkbox"
                  checked={editingCert ? editingCert.upload_required : newCert.upload_required}
                  onChange={(e) => editingCert
                    ? setEditingCert({...editingCert, upload_required: e.target.checked})
                    : setNewCert({...newCert, upload_required: e.target.checked})}
                  className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium">Certificate Upload Required</span>
              </label>
              <p className="text-xs text-gray-400 mt-1 ml-6">
                Uncheck if this certification doesn't require a file upload (e.g., for course completions or training attendance)
              </p>
            </div>
            
            <div className="flex gap-2 mt-4">
              <button
                onClick={editingCert ? handleUpdateCertification : handleAddCertification}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <Save className="h-4 w-4" />
                Save
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  setEditingCert(null);
                }}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Department Filter */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex gap-2">
            {DEPARTMENTS.map(dept => (
              <button
                key={dept}
                onClick={() => setSelectedDepartment(dept)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  selectedDepartment === dept
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {dept}
              </button>
            ))}
          </div>
        </div>

        {/* Tier Requirements */}
        <div className="p-6">
          {TIER_LEVELS.map(tier => (
            <div key={tier} className="mb-6">
              <button
                onClick={() => setExpandedTier(expandedTier === tier ? null : tier)}
                className="w-full flex items-center justify-between p-4 bg-gray-900 rounded-lg hover:bg-gray-850 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className={`${getTierColor(tier)} p-2 rounded-lg`}>
                    <Award className="h-6 w-6 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-lg font-semibold text-white">{tier === 0 ? 'New Hire (Tier 0)' : `Tier ${tier}`}</h3>
                    <p className="text-sm text-gray-400">
                      {certifications
                        .filter(cert => cert.tier_level === tier)
                        .filter(cert => selectedDepartment === 'All' || cert.department === selectedDepartment || cert.department === null)
                        .length} certifications
                    </p>
                  </div>
                </div>
                {expandedTier === tier ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
              </button>

              {expandedTier === tier && (
                <div className="mt-4 space-y-2">
                  {certifications
                    .filter(cert => cert.tier_level === tier)
                    .filter(cert => selectedDepartment === 'All' || cert.department === selectedDepartment || cert.department === null)
                    .map(cert => (
                      <div key={cert.id} className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                        <div>
                          <p className="text-white font-medium">{cert.name}</p>
                          <p className="text-sm text-gray-400">
                            {cert.code} • {cert.department || 'All Departments'} • {getValidityLabel(cert.validity_period)}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setEditingCert(cert)}
                            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteCertification(cert.id!)}
                            className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  {certifications.length === 0 && (
                    <p className="text-center text-gray-500 py-4">No certifications created yet</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
